import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Clock, Loader2, MessageSquarePlus } from 'lucide-react';
import { format } from 'date-fns';

const FollowUpModal = ({ download, onClose, onFollowUpAdded }) => {
  const [followUpText, setFollowUpText] = useState('');
  const [reflectionType, setReflectionType] = useState('update');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!followUpText.trim()) {
      toast.error('Please enter your reflection');
      return;
    }

    setIsSubmitting(true);
    try {
      await onFollowUpAdded(followUpText, reflectionType);
      toast.success('Follow-up saved', {
        description:
          'Open this download from History to see your reflection in the timeline.',
      });
      onClose();
    } catch (error) {
      toast.error('Failed to add follow-up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reflectionTypes = [
    { value: 'update', label: '📝 Update', description: 'How things have progressed' },
    { value: 'answer', label: '✅ Answer Received', description: 'God provided clarity' },
    { value: 'testimony', label: '🙏 Testimony', description: 'Guidance was confirmed' },
    { value: 'challenge', label: '⚠️ Challenge', description: 'Still seeking understanding' },
  ];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl glass-card border-[rgba(245,245,245,0.14)]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <MessageSquarePlus size={24} className="text-[var(--sd-primary)]" />
            Add Follow-Up Reflection
          </DialogTitle>
          <p className="text-sm text-[var(--sd-muted-fg)] mt-2">
            Build upon your spiritual journey and track how divine guidance unfolds over time
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Original Download Date */}
          <div className="flex items-center gap-2 text-sm text-[var(--sd-muted-fg)]">
            <Clock size={16} />
            <span>Original download: {format(new Date(download.created_at), 'MMM d, yyyy')}</span>
          </div>

          {/* Reflection Type */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Type of Reflection
            </label>
            <Select value={reflectionType} onValueChange={setReflectionType}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reflectionTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{type.label}</span>
                      <span className="text-xs text-[var(--sd-muted-fg)]">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Follow-up Text */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Your Reflection
            </label>
            <Textarea
              value={followUpText}
              onChange={(e) => setFollowUpText(e.target.value)}
              placeholder="Share what has happened since your original download. Has this guidance proven true? What has God revealed?"
              className="min-h-[200px] text-base"
              disabled={isSubmitting}
            />
            <p className="text-xs text-[var(--sd-muted-fg)] mt-2">
              {followUpText.length} characters
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-[rgba(184,120,132,0.08)] border border-[rgba(184,120,132,0.25)] rounded-lg p-4 space-y-2">
            <p className="text-sm text-[var(--sd-fg-secondary)]">
              <strong className="text-[var(--sd-primary)]">Where does it go?</strong>{' '}
              Your reflection is attached to this specific download. Open the entry from{' '}
              <strong className="text-[var(--sd-fg)]">History</strong> and scroll to the{' '}
              <strong className="text-[var(--sd-fg)]">Reflection Timeline</strong> at the bottom of the modal to see all follow-ups in chronological order.
            </p>
            <p className="text-sm text-[var(--sd-muted-fg)]">
              💡 <strong className="text-[var(--sd-primary)]">Tip:</strong> Regular follow-ups help you see patterns in how God works in your life and confirm divine guidance over time.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[rgba(245,245,245,0.10)]">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !followUpText.trim()}
            className="bg-[var(--sd-primary)] hover:bg-[#D7A1A8] text-[var(--sd-primary-fg)]"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <MessageSquarePlus size={16} className="mr-2" />
                Add Follow-Up
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FollowUpModal;
